<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanCardsAPI from 'dashboard/api/kanbanCards';
import KanbanCardNotes from './KanbanCardNotes.vue';
import KanbanCardChecklist from './KanbanCardChecklist.vue';
import KanbanCardAttachments from './KanbanCardAttachments.vue';
import KanbanCardProducts from './KanbanCardProducts.vue';
import KanbanCardHistory from './KanbanCardHistory.vue';
import KanbanCardActivity from './KanbanCardActivity.vue';

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    required: true,
  },
  kanbanCard: {
    type: Object,
    default: null,
  },
  kanbanColumns: {
    type: Array,
    default: () => [],
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});

// Emits
const emit = defineEmits(['close', 'update-conversation']);

const { t } = useI18n();

// Refs
const activeTab = ref('notes');

// Watch for changes in show prop
watch(
  () => props.show,
  newShow => {
    // Reset to notes tab when modal opens
    if (newShow) {
      activeTab.value = 'notes';
    }
  }
);

// Computed properties
const contactName = computed(() => {
  return props.conversation?.contact?.name || t('KANBAN.UNKNOWN_CONTACT');
});

const contactAvatar = computed(() => {
  return props.conversation?.contact?.avatar_url;
});

const contactInitial = computed(() => {
  const name = props.conversation?.contact?.name || t('KANBAN.UNKNOWN_CONTACT');
  return name.charAt(0).toUpperCase();
});

const assigneeName = computed(() => {
  return props.conversation?.assignee?.name || t('KANBAN.NO_ASSIGNEE');
});

const assigneeInitial = computed(() => {
  const name = props.conversation?.assignee?.name || '?';
  return name.charAt(0).toUpperCase();
});

const timeAgo = computed(() => {
  // Priorizar sempre o KanbanCard para mostrar a última atividade do kanban
  let lastActivity = null;

  // 1. Primeira prioridade: última atividade do KanbanCard
  if (props.kanbanCard?.last_activity_at) {
    lastActivity = props.kanbanCard.last_activity_at;
  }
  // 2. Segunda prioridade: última atividade específica do kanban da conversa
  else if (props.conversation?.last_kanban_activity_at) {
    lastActivity = props.conversation.last_kanban_activity_at;
  }
  // 3. Terceira prioridade: última atividade geral da conversa
  else if (props.conversation?.last_activity_at) {
    lastActivity = props.conversation.last_activity_at;
  }
  // 4. Fallback: data de atualização da conversa
  else if (props.conversation?.updated_at) {
    lastActivity = props.conversation.updated_at;
  }

  if (!lastActivity) {
    return t('KANBAN.NO_ACTIVITY');
  }

  const date = new Date(lastActivity);

  // Sempre mostrar data e hora exata
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const priorityLabel = computed(() => {
  if (!props.conversation?.priority) return t('KANBAN.NO_PRIORITY');

  const priorities = {
    urgent: t('KANBAN.PRIORITY.URGENT'),
    high: t('KANBAN.PRIORITY.HIGH'),
    medium: t('KANBAN.PRIORITY.MEDIUM'),
    low: t('KANBAN.PRIORITY.LOW'),
  };

  return priorities[props.conversation.priority] || props.conversation.priority;
});

const priorityColor = computed(() => {
  if (!props.conversation?.priority) return 'bg-gray-500';

  const colors = {
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500',
  };

  return colors[props.conversation.priority] || 'bg-gray-500';
});

// Methods
const handleClose = async event => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Usar nextTick para garantir que o fechamento seja assíncrono
  await nextTick();
  emit('close');
};

const handleBackdropClick = event => {
  if (event.target === event.currentTarget) {
    // IMPORTANTE: Parar propagação para evitar navigateToConversation
    event.stopPropagation();
    event.preventDefault();
    handleClose();
  }
};

// Função para trocar tabs
const setActiveTab = tab => {
  activeTab.value = tab;
};

const isTabActive = tab => {
  return activeTab.value === tab;
};

const handleUpdateConversation = async updatedConversation => {
  try {
    emit('update-conversation', updatedConversation);
  } catch (error) {
    console.error('Erro ao atualizar conversa:', error);
  }
};

// Status update methods
const updateCardStatus = async status => {
  if (!props.kanbanCard?.id) {
    console.error('Card ID not found');
    return;
  }

  try {
    const response = await KanbanCardsAPI.updateCardStatus(
      props.pipelineId,
      props.kanbanCard.id,
      status
    );

    // Update the card in the conversation
    const updatedConversation = { ...props.conversation };
    const cardIndex = updatedConversation.kanban_cards?.findIndex(
      card => String(card.kanban_pipeline_id) === String(props.pipelineId)
    );

    if (cardIndex !== -1 && updatedConversation.kanban_cards) {
      updatedConversation.kanban_cards[cardIndex] = {
        ...updatedConversation.kanban_cards[cardIndex],
        status: status,
      };
    }

    emit('update-conversation', updatedConversation);
  } catch (error) {
    console.error('Error updating card status:', error);
  }
};
</script>

<template>
  <div
    v-if="show"
    class="modal-overlay"
    style="z-index: 99999 !important"
    :style="{ display: 'flex !important' }"
    @click="handleBackdropClick"
  >
    <div class="modal-container" @click.stop>
      <!-- Header -->
      <div class="p-4 md:p-6 pb-4 border-b bg-n-background/50 backdrop-blur-sm">
        <!-- Top Row: Contact Info and Close Button -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3 min-w-0 flex-1">
            <!-- Contact Avatar and Info -->
            <div class="flex items-center gap-3">
              <div class="relative">
                <img
                  v-if="contactAvatar"
                  :src="contactAvatar"
                  :alt="contactName"
                  class="w-10 h-10 rounded-full object-cover ring-2 ring-n-blue-11/20"
                />
                <div
                  v-else
                  class="w-10 h-10 rounded-full bg-n-blue-11/10 flex items-center justify-center text-n-blue-11 font-semibold ring-2 ring-n-blue-11/20"
                >
                  {{ contactInitial }}
                </div>
              </div>
              <div class="min-w-0">
                <h2
                  class="text-base md:text-lg font-medium text-n-slate-12 truncate"
                >
                  {{ contactName }}
                </h2>
                <div class="flex items-center gap-2 flex-wrap">
                  <div
                    class="px-2 py-1 rounded-full text-xs font-medium border"
                    :class="
                      priorityColor === 'bg-red-500'
                        ? 'bg-red-100 text-red-700 border-red-200'
                        : priorityColor === 'bg-orange-500'
                          ? 'bg-orange-100 text-orange-700 border-orange-200'
                          : priorityColor === 'bg-yellow-500'
                            ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                            : priorityColor === 'bg-green-500'
                              ? 'bg-green-100 text-green-700 border-green-200'
                              : 'bg-n-slate-2 text-n-slate-11 border-n-weak'
                    "
                  >
                    {{ priorityLabel }}
                  </div>
                  <span class="text-xs text-n-slate-11">
                    Atualizado em: {{ timeAgo }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Status Actions -->
          <div v-if="kanbanCard" class="flex items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12">Status:</span>
            <div class="flex items-center gap-2">
              <button
                class="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 border border-green-300 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                :class="{
                  'bg-green-200 border-green-400 shadow-sm':
                    kanbanCard?.status === 'won',
                }"
                @click="updateCardStatus('won')"
              >
                <i class="i-lucide-check-circle size-4" />
                Ganho
              </button>
              <button
                class="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                :class="{
                  'bg-red-200 border-red-400 shadow-sm':
                    kanbanCard?.status === 'lost',
                }"
                @click="updateCardStatus('lost')"
              >
                <i class="i-lucide-x-circle size-4" />
                Perdido
              </button>
              <button
                class="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                :class="{
                  'bg-blue-200 border-blue-400 shadow-sm':
                    kanbanCard?.status === 'open',
                }"
                @click="updateCardStatus('open')"
              >
                <i class="i-lucide-circle size-4" />
                Em Aberto
              </button>

              <!-- Reopen button - only show if card is won or lost -->
              <button
                v-if="
                  kanbanCard?.status === 'won' || kanbanCard?.status === 'lost'
                "
                class="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-300 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                title="Reabrir este card"
                @click="updateCardStatus('open')"
              >
                <i class="i-lucide-rotate-ccw size-4" />
                Reabrir
              </button>
            </div>
          </div>

          <!-- Close Button -->
          <button
            class="w-8 h-8 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12 transition-colors"
            @click="handleClose"
          >
            <i class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Card History Section -->
        <div class="mt-4 p-4 bg-n-slate-1/30 rounded-lg border border-n-weak">
          <div class="flex items-center gap-2 mb-3">
            <i class="i-lucide-history size-4 text-n-blue-11" />
            <h3 class="font-medium text-n-slate-12">Histórico do Card</h3>
          </div>
          <KanbanCardHistory
            :conversation="conversation"
            :kanban-card="kanbanCard"
            :kanban-columns="kanbanColumns"
            :pipeline-id="pipelineId"
            @update-conversation="handleUpdateConversation"
          />
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Left Panel -->
        <div class="flex-1 p-4 md:p-6 overflow-y-auto">
          <!-- Tabs Section -->
          <div class="space-y-4">
            <!-- Tab Navigation -->
            <div class="flex bg-n-slate-1/50 p-1 rounded-lg">
              <button
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200"
                :class="
                  isTabActive('notes')
                    ? 'bg-n-background text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-1'
                "
                @click="setActiveTab('notes')"
              >
                <i class="i-lucide-file-text size-4" />
                <span class="hidden sm:inline">Notas do Card</span>
              </button>

              <button
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200"
                :class="
                  isTabActive('checklist')
                    ? 'bg-n-background text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-1'
                "
                @click="setActiveTab('checklist')"
              >
                <i class="i-lucide-check-square size-4" />
                <span class="hidden sm:inline">Checklist do Card</span>
              </button>

              <button
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200"
                :class="
                  isTabActive('attachments')
                    ? 'bg-n-background text-n-slate-12 shadow-sm'
                    : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-1'
                "
                @click="setActiveTab('attachments')"
              >
                <i class="i-lucide-paperclip size-4" />
                <span class="hidden sm:inline">Anexos do Card</span>
              </button>
            </div>

            <!-- Tab Content -->
            <div class="animate-in fade-in-50 duration-200">
              <KanbanCardNotes
                v-if="activeTab === 'notes'"
                :conversation="conversation"
                :kanban-card="kanbanCard"
                :pipeline-id="pipelineId"
                @update-conversation="handleUpdateConversation"
              />
              <KanbanCardChecklist
                v-else-if="activeTab === 'checklist'"
                :conversation="conversation"
                :kanban-card="kanbanCard"
                :pipeline-id="pipelineId"
                @update-conversation="handleUpdateConversation"
              />
              <KanbanCardAttachments
                v-else-if="activeTab === 'attachments'"
                :conversation="conversation"
                :kanban-card="kanbanCard"
                :pipeline-id="pipelineId"
                @update-conversation="handleUpdateConversation"
              />
            </div>
          </div>
        </div>

        <!-- Right Sidebar -->
        <div
          class="w-80 lg:w-96 border-l border-n-weak bg-gradient-to-b from-n-slate-1/20 to-n-slate-1/30 p-4 md:p-6 overflow-y-auto hidden md:block"
        >
          <!-- Assigned Agent -->
          <div v-if="conversation?.assignee" class="mb-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
                <i class="i-lucide-user size-4 text-n-blue-11" />
                Agente Atribuído
              </h3>
            </div>
            <div
              class="flex items-center gap-3 p-4 bg-n-background rounded-lg border border-n-weak hover:shadow-md transition-all group"
            >
              <div class="relative">
                <img
                  v-if="conversation.assignee.avatar_url"
                  :src="conversation.assignee.avatar_url"
                  :alt="assigneeName"
                  class="w-10 h-10 rounded-full object-cover ring-2 ring-n-blue-11/20"
                />
                <div
                  v-else
                  class="w-10 h-10 rounded-full bg-n-blue-11 flex items-center justify-center text-white font-semibold ring-2 ring-n-blue-11/20"
                >
                  {{ assigneeInitial }}
                </div>
                <div
                  class="absolute -top-1 -right-1 w-4 h-4 bg-green-400 border-2 border-n-background rounded-full"
                />
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <span class="font-medium text-n-slate-12">{{
                    assigneeName
                  }}</span>
                  <div
                    class="px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 rounded-full text-xs font-medium"
                  >
                    Online
                  </div>
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ conversation.assignee.email || 'Sem email' }}
                </div>
              </div>
            </div>
          </div>

          <!-- No Assignee State -->
          <div v-else class="mb-6">
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
                <i class="i-lucide-user size-4 text-n-blue-11" />
                Agente Atribuído
              </h3>
            </div>
            <div class="text-center py-6">
              <div
                class="w-12 h-12 mx-auto mb-3 bg-n-slate-2 rounded-full flex items-center justify-center"
              >
                <i class="i-lucide-user-plus size-6 text-n-slate-9" />
              </div>
              <p class="text-sm text-n-slate-11">Nenhum agente atribuído</p>
              <button
                class="mt-2 text-xs text-n-blue-11 hover:text-n-blue-12 font-medium"
              >
                Atribuir agente
              </button>
            </div>
          </div>

          <!-- Products Card -->
          <div
            class="bg-n-background rounded-lg border border-n-weak hover:shadow-md transition-shadow mb-6"
          >
            <div class="p-4 pb-3 border-b border-n-weak">
              <h4
                class="text-base font-semibold text-n-slate-12 flex items-center gap-2"
              >
                <i class="i-lucide-shopping-cart size-4 text-n-green-11" />
                Produtos
              </h4>
            </div>
            <div class="p-4">
              <KanbanCardProducts
                :conversation="conversation"
                :kanban-card="kanbanCard"
                :pipeline-id="pipelineId"
                @update-conversation="handleUpdateConversation"
              />
            </div>
          </div>

          <!-- Activities Card -->
          <div
            class="bg-n-background rounded-lg border border-n-weak hover:shadow-md transition-shadow mb-6"
          >
            <div class="p-4 pb-3 border-b border-n-weak">
              <h4
                class="text-base font-semibold text-n-slate-12 flex items-center gap-2"
              >
                <i class="i-lucide-activity size-4 text-n-blue-11" />
                Atividades Recentes
              </h4>
            </div>
            <div class="p-4">
              <KanbanCardActivity
                :conversation="conversation"
                :kanban-card="kanbanCard"
                :pipeline-id="pipelineId"
                :limit="5"
                @update-conversation="handleUpdateConversation"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  pointer-events: auto;
}

.modal-container {
  background: var(--n-background) !important;
  border: 1px solid var(--n-weak) !important;
  border-radius: 12px;
  width: 95vw;
  max-width: 1400px;
  height: 95vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1001;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

/* Dark mode styles */
@media (prefers-color-scheme: dark) {
  .modal-container {
    background: #1a1a1a !important;
    border-color: #2a2a2a !important;
  }
}

/* Custom scrollbar */
.modal-container::-webkit-scrollbar,
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.modal-container::-webkit-scrollbar-track,
.overflow-y-auto::-webkit-scrollbar-track {
  background: var(--n-slate-2);
}

.modal-container::-webkit-scrollbar-thumb,
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: var(--n-slate-8);
  border-radius: 3px;
}

.modal-container::-webkit-scrollbar-thumb:hover,
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: var(--n-slate-9);
}

/* Ensure sidebar scroll works */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: var(--n-slate-8) var(--n-slate-2);
}

/* Animation classes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-in {
  animation: fadeIn 0.2s ease-out;
}

.fade-in-50 {
  animation-duration: 0.2s;
}
</style>
